import type { ReactNode } from 'react';
import type { PersonId } from '@shared/brand';
import { usePersonLink } from '../../hooks/usePersonLink';

/**
 * El nombre de quien reportó un dato, que lleva a su ficha.
 *
 * Existe por un clic que no hacía nada. El nombre ya era un botón, con su
 * estilo de enlace, y su manejador era `() => { openPerson(id); }`. Pero
 * `usePersonLink` no navega: **devuelve** la función que navega, o `null` si
 * quien mira no puede entrar. Llamarlo así creaba el navegador y lo tiraba. Los
 * otros seis usos de la pantalla lo hacían bien —`onOpen={openPerson(id)}`— y
 * TypeScript no marca un valor de retorno ignorado, así que éste pasó.
 *
 * Y había una segunda mitad: sin `ver_detalle` el botón se seguía pintando como
 * pulsable, que es justo lo que `usePersonLink` dice que no hay que hacer — un
 * nombre que parece puerta y no abre promete algo que no puede cumplir. Sin
 * permiso, aquí queda como texto.
 */
export const ReportedPersonName = ({
  personId,
  name,
  className,
}: {
  readonly personId: PersonId;
  readonly name: string;
  /* `undefined` explícito: una clase de CSS Modules es `string | undefined` en
     este repo, y con `exactOptionalPropertyTypes` opcional no lo admite. */
  readonly className: string | undefined;
}): ReactNode => {
  const openPerson = usePersonLink();
  const abrir = openPerson(personId);

  if (abrir === null) return <span className={className}>{name}</span>;

  return (
    <button type="button" className={className} onClick={abrir}>
      {name}
    </button>
  );
};
